
// planet-aspects-stats.js
// Calcolo statistico: correlazione tra eventi (aspetti/retro) e comportamento del mercato.
// - Ricerca tollerante della barra OHLC più vicina al timestamp evento.
// - Tolleranza basata sullo step mediano tra barre.
// - Output: oggetto 'stats' e funzione per tabella HTML.

/**
 * Calcola statistiche per ciascun tipo di evento/aspetto.
 *
 * @param {Array<{aspectKey:string, start?:number, time?:number}>} events
 *   Eventi su tutto il dataset. Per gli aspetti: { start, end, aspectKey, pair }
 *   Per le retrogradazioni: { start, end, aspectKey:'RETRO', pair: '<PIANETA>' }
 *   NB: l’analisi usa l’anchor al tempo 'start' se disponibile, altrimenti 'time'.
 *
 * @param {Array<{time:number, open:number, high:number, low:number, close:number}>} ohlcData
 *   Dati OHLC su tutto il dataset, ordinati per 'time' crescente. 'time' in secondi UNIX.
 *
 * @param {Object} params
 * @param {number} [params.windowSize=10]  Numero di barre a sinistra e a destra (±) per identificare max/min locale.
 * @param {number} [params.accelBars=5]    Numero di barre future per valutare l’accelerazione.
 * @param {number} [params.accelThreshold=0.03] Soglia (in decimale) per accelerazione (es. 0.03 = 3%).
 *
 * @returns {Record<string, {maxCount:number, minCount:number, upAccel:number, downAccel:number, total:number}>}
 */
export function computeAspectStats(events, ohlcData, params = {}) {
  const {
    windowSize = 10,
    accelBars = 5,
    accelThreshold = 0.03
  } = params;

  if (!Array.isArray(events) || !events.length) return {};
  if (!Array.isArray(ohlcData) || !ohlcData.length) return {};

  // Mappa time->index (per match esatto quando possibile)
  const indexByTime = new Map();
  for (let i = 0; i < ohlcData.length; i++) indexByTime.set(ohlcData[i].time, i);

  // Step mediano tra le barre (secondi) e tolleranza = metà step (almeno 1s)
  const stepSec = medianStepSec(ohlcData);
  const tolSec = stepSec ? Math.max(1, Math.floor(stepSec / 2)) : 1;

  const stats = Object.create(null);

  for (const ev of events) {
    const aspKey = ev.aspectKey || 'UNKNOWN';
    if (!stats[aspKey]) {
      stats[aspKey] = { maxCount: 0, minCount: 0, upAccel: 0, downAccel: 0, total: 0 };
    }
    stats[aspKey].total++;

    const anchorTime = (typeof ev.start === 'number' ? ev.start : ev.time);
    if (!Number.isFinite(anchorTime)) continue;

    // Prova match esatto, altrimenti cerca la barra più vicina entro tolleranza
    let idx = indexByTime.get(anchorTime);
    if (idx == null) idx = findNearestBarIndex(ohlcData, anchorTime, tolSec);
    if (idx === -1) continue; // nessuna barra vicina: salta

    // 1) Max/Min locale nella finestra ±windowSize
    const i0 = Math.max(0, idx - windowSize);
    const i1 = Math.min(ohlcData.length, idx + windowSize + 1);
    let high = -Infinity, low = +Infinity;
    for (let j = i0; j < i1; j++) {
      const h = ohlcData[j].high, l = ohlcData[j].low;
      if (h > high) high = h;
      if (l < low)  low  = l;
    }
    const px = ohlcData[idx].close;
    if (px === high) stats[aspKey].maxCount++;
    if (px === low)  stats[aspKey].minCount++;

    // 2) Accelerazione futura su 'accelBars'
    const k1 = Math.min(ohlcData.length, idx + accelBars);
    if (k1 - idx >= 1) {
      const p2 = ohlcData[k1 - 1].close;
      const change = (p2 - px) / px;
      if (change >= accelThreshold) stats[aspKey].upAccel++;
      if (change <= -accelThreshold) stats[aspKey].downAccel++;
    }
  }

  return stats;
}

/**
 * Calcola lo step mediano tra i timestamp delle barre (in secondi).
 * @param {Array<{time:number}>} ohlcData
 * @returns {number} step mediano (sec), 0 se non calcolabile
 */
function medianStepSec(ohlcData) {
  const diffs = [];
  for (let i = 1; i < ohlcData.length; i++) {
    const d = ohlcData[i].time - ohlcData[i - 1].time;
    if (d > 0 && Number.isFinite(d)) diffs.push(d);
  }
  if (!diffs.length) return 0;
  diffs.sort((a, b) => a - b);
  const mid = Math.floor(diffs.length / 2);
  return diffs.length % 2 ? diffs[mid] : Math.round((diffs[mid - 1] + diffs[mid]) / 2);
}

/**
 * Trova l’indice della barra più vicina a 'time' entro 'toleranceSec'.
 * Usa binary search per efficienza, poi confronta i candidati adiacenti.
 *
 * @param {Array<{time:number}>} ohlcData
 * @param {number} time  timestamp evento (sec)
 * @param {number} toleranceSec  tolleranza massima (sec)
 * @returns {number} indice barra o -1 se nessuna entro tolleranza
 */
function findNearestBarIndex(ohlcData, time, toleranceSec) {
  let lo = 0, hi = ohlcData.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const tmid = ohlcData[mid].time;
    if (tmid === time) return mid;
    if (tmid < time) lo = mid + 1; else hi = mid - 1;
  }
  // candidati: 'lo' (successivo) e 'hi' (precedente)
  const cand = [];
  if (lo < ohlcData.length) cand.push(lo);
  if (hi >= 0) cand.push(hi);
  let best = -1, bestDiff = Infinity;
  for (const i of cand) {
    const diff = Math.abs(ohlcData[i].time - time);
    if (diff < bestDiff) { bestDiff = diff; best = i; }
  }
  return (bestDiff <= toleranceSec) ? best : -1;
}

/**
 * Genera tabella HTML (elemento <table>) con le percentuali per ciascun aspetto.
 *
 * @param {Record<string, {maxCount:number, minCount:number, upAccel:number, downAccel:number, total:number}>} stats
 * @param {Object} [opts]
 * @param {string} [opts.className]  classe CSS da assegnare alla tabella (opzionale)
 * @returns {HTMLTableElement}
 */
export function renderStatsTable(stats, opts = {}) {
  const cls = opts.className || '';
  const table = document.createElement('table');
  if (cls) table.className = cls;

  const rowsHtml = Object.entries(stats).map(([aspKey, s]) => {
    const tot = s.total || 1;
    const pct = v => ((v / tot) * 100).toFixed(1) + '%';
    return `
      <tr>
        <td>${aspKey}</td>
        <td>${tot}</td>
        <td>${pct(s.maxCount)}</td>
        <td>${pct(s.minCount)}</td>
        <td>${pct(s.upAccel)}</td>
        <td>${pct(s.downAccel)}</td>
      </tr>`;
  }).join('');

  table.innerHTML = `
    <thead>
      <tr>
        <th>Aspetto</th>
        <th>Totale</th>
        <th>Massimo</th>
        <th>Minimo</th>
        <th>Accel. ↑</th>
        <th>Accel. ↓</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
    </tbody>
  `;
  return table;
}
